import styled from "styled-components";

import {
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../utils/constants";
import { AmountVariant } from "./types";
import { BaseButton } from "../../../../components/Button";

const variantColor = (variant: AmountVariant): string => {
  if (variant === "income") {
    return "var(--color-success)";
  }
  if (variant === "expense") {
    return "var(--color-error)";
  }
  return "var(--color-text-default)";
};

const variantBackground = (variant: AmountVariant): string => {
  if (variant === "income") {
    return "var(--color-success-background)";
  }
  if (variant === "expense") {
    return "var(--color-error-background)";
  }
  return "var(--hover-background)";
};

const variantBorder = (variant: AmountVariant): string => {
  if (variant === "income") {
    return "var(--color-success-border)";
  }
  if (variant === "expense") {
    return "var(--color-error-border)";
  }
  return "var(--color-border-light)";
};

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-background-2);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  overflow: hidden;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
  }
`;

/* ------------- Desktop ------------- */

export const Table = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TableHead = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr 1.3fr 1.4fr 0.9fr 40px;
  gap: ${SPACING.md}px;
  align-items: center;
  padding: ${SPACING.sm}px ${SPACING.lg}px;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-background-2);
`;

export const TableHeadCell = styled.span<{ $align?: "right" }>`
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  text-align: ${({ $align }) => $align ?? "left"};
`;

export const DesktopGroupHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.sm}px ${SPACING.lg}px;
  background: var(--color-background-3);
  border-top: 1px solid var(--color-border-light);
  border-bottom: 1px solid var(--color-border-light);
`;

export const DesktopGroupHeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${SPACING.md}px;
  min-width: 0;
`;

export const DesktopGroupTitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-weight: 700;
  font-size: var(--text-md);
  color: var(--color-text-default);
  letter-spacing: -0.005em;
`;

export const DesktopGroupCount = styled.span`
  font-style: italic;
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted);
`;

export const DesktopRowWrapper = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr 1.3fr 1.4fr 0.9fr 40px;
  gap: ${SPACING.md}px;
  align-items: center;
  padding: ${SPACING.md}px ${SPACING.lg}px;
  border-top: 1px solid var(--color-border-light);
  transition: background 0.15s ease;

  &:first-of-type {
    border-top: none;
  }

  &:hover {
    background: var(--hover-background);
  }
`;

export const DateText = styled.span`
  font-size: var(--text-base);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-secondary);
`;

export const CategoryCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const CategoryName = styled.span`
  font-weight: 700;
  font-size: var(--text-base);
  color: var(--color-text-default);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SubCategoryName = styled.span`
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const NoteText = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-style: italic;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const NoteCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const CategoryDot = styled.span<{ $color: string }>`
  flex-shrink: 0;
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

export const Amount = styled.span<{ $variant: AmountVariant; $bold?: boolean }>`
  font-family: "Crimson Pro", sans-serif;
  font-weight: ${({ $bold }) => ($bold ? 700 : 600)};
  font-size: var(--text-md);
  font-variant-numeric: tabular-nums;
  color: ${({ $variant }) => variantColor($variant)};
  text-align: right;
  white-space: nowrap;
`;

/* ------------- Account chip ------------- */

export const AccountChipWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xxs}px ${SPACING.sm}px;
  border-radius: 999px;
  border: 1px solid var(--color-border-light);
  background: var(--color-background-2);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-default);
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AccountDot = styled.span<{ $color: string }>`
  flex-shrink: 0;
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

/* ------------- Type pill (mobile) ------------- */

export const TypePillWrapper = styled.span<{ $variant: AmountVariant }>`
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xxs}px ${SPACING.sm}px;
  border-radius: 999px;
  border: 1px solid ${({ $variant }) => variantBorder($variant)};
  background: ${({ $variant }) => variantBackground($variant)};
  color: ${({ $variant }) => variantColor($variant)};
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

/* ------------- Mobile cards ------------- */

export const MobileList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MobileGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.sm}px ${SPACING.md}px;
  background: var(--color-background-3);
  border-top: 1px solid var(--color-border-light);
  border-bottom: 1px solid var(--color-border-light);
`;

export const MobileGroupLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const MobileGroupTitle = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-weight: 700;
  font-size: var(--text-md);
  color: var(--color-text-default);
`;

export const MobileGroupCount = styled.span`
  font-style: italic;
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted);
`;

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  border-top: 1px solid var(--color-border-light);

  &:first-of-type {
    border-top: none;
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardTopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const CardMiddle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

export const CardCategoryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const CardCategoryName = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-weight: 700;
  font-size: var(--text-md);
  color: var(--color-text-default);
`;

export const CardSubCategory = styled.span`
  font-size: var(--text-xs);
  color: var(--color-text-muted);
`;

export const CardAmount = styled.span<{ $variant: AmountVariant }>`
  font-family: "Crimson Pro", sans-serif;
  font-weight: 700;
  font-size: var(--text-lg);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${({ $variant }) => variantColor($variant)};
`;

export const CardNote = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-style: italic;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

/* ------------- Menu ------------- */

export const MenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
  justify-self: end;
`;

export const Menu = styled.div<{ $top: number; $right: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  right: ${({ $right }) => $right}px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  min-width: 140px;
  padding: ${SPACING.xs}px;
  border-radius: 10px;
  border: 1px solid var(--color-border-light);
  background: var(--color-card-background);
  box-shadow: var(--shadow-medium);
`;

export const MenuItem = styled(BaseButton)<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  width: 100%;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border: none;
  border-radius: 6px;
  background: none;
  text-align: left;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  cursor: pointer;
  color: ${({ $danger }) =>
    $danger ? "var(--color-error)" : "var(--color-text-default)"};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? "var(--color-error-background)" : "var(--hover-background)"};
  }

  & > svg {
    font-size: var(--text-md);
  }
`;

/* ------------- Footer ------------- */

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px ${SPACING.lg}px;
  border-top: 1px solid var(--color-border-light);
  background: var(--color-background-2);

  @media ${QUERIES.tabletAndUp} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

export const FooterText = styled.span`
  font-style: italic;
  font-size: var(--text-base);
  font-variant-numeric: tabular-nums;
  color: var(--color-text-muted);
  text-align: center;
`;

export const LoadMoreWrapper = styled.div`
  width: 100%;

  @media ${QUERIES.tabletAndUp} {
    width: auto;
  }
`;

export const LoadMoreLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
`;

/* ------------- Skeleton ------------- */

export const SkeletonRow = styled.div`
  height: 56px;
  border-top: 1px solid var(--color-border-light);
  background: linear-gradient(
    90deg,
    var(--hover-background) 0%,
    var(--color-card-background) 50%,
    var(--hover-background) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;

  &:first-of-type {
    border-top: none;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
